// LocaleProvider — exposes the active locale to the React tree.
//
// During SSR (prerender) the locale is derived from the explicit `ssrPath`
// passed in by the prerender script. During CSR the locale is derived from
// `window.location.pathname` and is updated whenever the URL changes.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_LOCALE,
  LOCALE_BCP47,
  LOCALE_INTL,
  parseLocaleFromPath,
  withLocale,
  type Locale,
} from "./locale";

interface LocaleContextValue {
  locale: Locale;
  /** BCP 47 tag suitable for <html lang>. */
  bcp47: string;
  /** Intl tag for Intl.NumberFormat / DateTimeFormat. */
  intlTag: string;
  /** Build a locale-prefixed URL for the active locale. */
  localizePath: (path: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
  /** Explicit locale (used by SSR); when omitted, derived from URL. */
  initialLocale?: Locale;
  /** Path used at SSR time to derive locale before window exists. */
  ssrPath?: string;
  children: ReactNode;
}

function deriveFromCurrentUrl(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  return parseLocaleFromPath(window.location.pathname).locale;
}

export function LocaleProvider({
  initialLocale,
  ssrPath,
  children,
}: LocaleProviderProps) {
  const seed: Locale =
    initialLocale ??
    (ssrPath ? parseLocaleFromPath(ssrPath).locale : deriveFromCurrentUrl());

  const [locale, setLocale] = useState<Locale>(seed);

  // Track URL changes on the client (popstate, History API navigation that
  // dispatches location-change events, hash changes — wouter-driven nav
  // dispatches `popstate` so we listen for that).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => {
      const next = parseLocaleFromPath(window.location.pathname).locale;
      setLocale((prev) => (prev === next ? prev : next));
    };
    update();
    window.addEventListener("popstate", update);
    // Patch pushState/replaceState so wouter Link clicks update the locale
    // without a full reload. We dispatch a synthetic event after both.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args as Parameters<typeof origPush>);
      window.dispatchEvent(new Event("popstate"));
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args as Parameters<typeof origReplace>);
      window.dispatchEvent(new Event("popstate"));
    };
    return () => {
      window.removeEventListener("popstate", update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  // Reflect the active locale on <html lang> at runtime so screen readers /
  // browsers / page-translation extensions see the right language. SSR sets
  // a default `lang` in the prerender shell and SeoHead overrides it.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = LOCALE_BCP47[locale];
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      bcp47: LOCALE_BCP47[locale],
      intlTag: LOCALE_INTL[locale],
      localizePath: (path: string) => withLocale(locale, path),
    }),
    [locale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Defensive default — never crash a page if the provider is missing
    // (e.g. an isolated unit test). Falls back to English unprefixed paths.
    return {
      locale: DEFAULT_LOCALE,
      bcp47: LOCALE_BCP47[DEFAULT_LOCALE],
      intlTag: LOCALE_INTL[DEFAULT_LOCALE],
      localizePath: (p) => p,
    };
  }
  return ctx;
}
